import { Search, MapPin, Star, Phone } from "lucide-react";

/**
 * Premium coded "local map pack" mockup: a realistic Google-style local search
 * results panel with the business ranked #1. Used as a hero visual (crisp, fast,
 * on-brand) instead of a generic 3D illustration.
 */
const Stars = ({ filled = 5, muted = false }: { filled?: number; muted?: boolean }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-3 h-3 ${
          i < filled
            ? muted
              ? "fill-slate-300 text-slate-300"
              : "fill-amber-400 text-amber-400"
            : "fill-slate-200 text-slate-200"
        }`}
      />
    ))}
  </div>
);

export const LocalPackMockup = () => {
  return (
    <div className="relative w-full max-w-[400px] mx-auto select-none">
      {/* soft brand glow */}
      <div className="absolute -inset-8 -z-10 bg-[radial-gradient(circle_at_70%_30%,rgba(0,102,255,0.18),transparent_60%),radial-gradient(circle_at_30%_80%,rgba(127,0,255,0.14),transparent_60%)] blur-2xl" />

      {/* floating result badge */}
      <div className="absolute -top-3 right-4 z-10 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 px-3.5 py-1.5 text-xs font-bold text-white shadow-lg shadow-blue-900/20">
        #1 in the Map Pack
      </div>

      <div className="relative overflow-hidden rounded-[1.75rem] border border-black/[0.06] bg-white shadow-2xl shadow-blue-900/10">
        {/* search bar */}
        <div className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-3.5">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">plumber near me</span>
          <span className="ml-auto flex items-center gap-1 text-xs text-slate-600">
            <MapPin className="h-3 w-3" /> Thunder Bay
          </span>
        </div>

        {/* results */}
        <div className="space-y-2 p-3">
          {/* #1: highlighted */}
          <div className="flex items-center gap-3 rounded-2xl border border-blue-200/80 bg-blue-50/70 p-3">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 text-base font-bold text-white shadow-md shadow-blue-500/30">
              1
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-bold text-slate-900">Your Business</div>
              <div className="mt-0.5 flex items-center gap-1.5">
                <Stars filled={5} />
                <span className="text-xs text-slate-600">5.0 · Open now</span>
              </div>
            </div>
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-blue-600 text-white shadow-md shadow-blue-600/30">
              <Phone className="h-4 w-4" />
            </div>
          </div>

          {/* #2, #3: muted competitors */}
          {[2, 3].map((n) => (
            <div key={n} className="flex items-center gap-3 rounded-2xl p-3 opacity-70">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-slate-100 text-base font-bold text-slate-400">
                {n}
              </div>
              <div className="min-w-0 flex-1">
                <div className="h-2.5 w-28 rounded-full bg-slate-200" />
                <div className="mt-2 flex items-center gap-1.5">
                  <Stars filled={4} muted />
                  <div className="h-2 w-10 rounded-full bg-slate-100" />
                </div>
              </div>
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-300">
                <Phone className="h-4 w-4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LocalPackMockup;
